<template>
  <v-container fluid class="auth-wrapper pa-0">
    <v-row class="fill-height" align="center" justify="center">
      <v-col cols="12" sm="8" md="4">
        <v-card class="login-card pa-10" elevation="0">

          <div class="mb-8 text-center">
            <img src="@/assets/logoSemFundo.png" alt="Logo" width="82" height="70" />
            <h1 class="brand-title">Cargas<span style="color:#16b37c">Zap</span></h1>
            <p class="login-subtitle">Acesse sua conta</p>
          </div>

          <v-form v-model="valid" ref="form" autocomplete="off">

            <!-- Bloqueia autofill -->
            <input type="text" style="display:none">
            <input type="password" style="display:none">

            <v-text-field
              v-model="email"
              label="E-mail"
              prepend-inner-icon="mdi-email-outline"
              :rules="emailRules"
              outlined
              dense
              dark
              autocomplete="new-email"
              autocorrect="off"
              autocapitalize="off"
              spellcheck="false"
              class="mb-0"
            />

            <v-text-field
              v-model="password"
              label="Senha"
              prepend-inner-icon="mdi-lock-outline"
              :append-icon="showPassword ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
              :type="showPassword ? 'text' : 'password'"
              :rules="passwordRules"
              outlined
              dense
              dark
              autocomplete="new-password"
              autocorrect="off"
              autocapitalize="off"
              spellcheck="false"
              class="mb-4"
              @click:append="showPassword = !showPassword"
            />

            <v-btn
              block
              large
              depressed
              dark
              class="login-btn mb-4"
              :loading="loading"
              @click="login"
            >
              Entrar
            </v-btn>

            <div id="google-login-btn" class="google-btn mb-6"></div>

          </v-form>

          <div class="text-center">
            <router-link to="/cadastro" class="auth-link">
              Criar nova conta
            </router-link>
          </div>

        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
export default {
  name: "LoginPage",
  data() {
    return {
      valid: false,
      email: "",
      password: "",
      loading: false,
      showPassword: false,
      emailRules: [
        v => !!v || "E-mail obrigatório",
        v => /.+@.+\..+/.test(v) || "E-mail inválido"
      ],
      passwordRules: [
        v => !!v || "Senha obrigatória"
      ]
    };
  },

  methods: {
    async login() {
      const isValid = this.$refs.form ? this.$refs.form.validate() : this.valid;
      if (!isValid) return;

      this.loading = true;
      try {
        const { loginUser } = await import('./loginServices');
        const { default: authService } = await import('../../services/auth');

        const { data } = await loginUser({ email: this.email, senha: this.password });

        authService.setToken(data.token);
        this.$store.commit('auth/setToken', data.token);
        this.$store.commit('auth/setUser', data.user);

        const redirectPath = this.$route.query.redirect || '/inicio';
        this.$router.push(redirectPath);
        this.$toasted.show('Login realizado com sucesso!', { type: 'success' });
      } catch (err) {
        const msg = err.response?.data?.error || 'Erro ao realizar login';
        this.$toasted.show(msg, { type: 'error' });
      } finally {
        this.loading = false;
      }
    },

    async handleCredentialResponse(response) {
      this.loading = true;
      try {
        const profile = this.decodeJwtPayload(response.credential);
        const { loginUserGoogle } = await import('./loginGoogleService');
        const { default: authService } = await import('../../services/auth');

        const { data } = await loginUserGoogle({
          google_id: profile.sub,
          email: profile.email,
        });

        authService.setToken(data.token);
        this.$store.commit('auth/setToken', data.token);
        this.$store.commit('auth/setUser', data.user);

        const redirectPath = this.$route.query.redirect || '/inicio';
        this.$router.push(redirectPath);
        this.$toasted.show('Login com Google realizado!', { type: 'success' });
      } catch (err) {
        const msg = err.response?.data?.error || 'Erro ao entrar com Google';
        this.$toasted.show(msg, { type: 'error' });
      } finally {
        this.loading = false;
      }
    },

    decodeJwtPayload(token) {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map((c) => {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      return JSON.parse(jsonPayload);
    }
  },

  mounted() {
    if (window.google && window.google.accounts) {
      window.google.accounts.id.initialize({
        client_id: '165786893140-rrhev66adkeek95lrinf7fqjurda7ma2.apps.googleusercontent.com',
        callback: this.handleCredentialResponse,
      });
      window.google.accounts.id.renderButton(
        document.getElementById('google-login-btn'),
        { theme: 'outline', size: 'large', width: 340 }
      );
    }
  }
};
</script>

<style scoped>

/* ============================= */
/* FUNDO GERAL */
/* ============================= */

.auth-wrapper {
  min-height: 100vh;
  background: #0b1220;
  font-family: 'Inter', sans-serif;
  display: flex;
  align-items: center;
}

/* ============================= */
/* CARD */
/* ============================= */

.login-card {
  width: 100%;
  max-width: 420px;
  background: #0f172a;
  border-radius: 20px;
  border: 1px solid #1f2937;
  box-shadow: 0 30px 80px rgba(0,0,0,0.6);
}

/* ============================= */
/* TEXTOS */
/* ============================= */

.brand-title {
  font-size: 1.8rem;
  font-weight: 700;
  color: #ffffff;
}

.login-subtitle {
  color: #94a3b8;
  font-size: 0.95rem;
}

/* ============================= */
/* INPUTS */
/* ============================= */

::v-deep .v-text-field--outlined .v-input__control {
  background: #111827;
  border-radius: 14px;
}

::v-deep .v-text-field--outlined fieldset {
  border-color: #1f2937;
  transition: 0.2s ease;
}

::v-deep .v-text-field--outlined.v-input--is-focused fieldset {
  border-color: #11b981;
  box-shadow: 0 0 0 3px rgba(17,185,129,0.15);
}

::v-deep input {
  color: #e5e7eb !important;
}

::v-deep .v-label {
  color: #94a3b8 !important;
}

::v-deep .error--text {
  color: #f43f5e !important;
}

/* Remove fundo branco autofill Chrome */
::v-deep input:-webkit-autofill,
::v-deep input:-webkit-autofill:hover,
::v-deep input:-webkit-autofill:focus {
  -webkit-box-shadow: 0 0 0 1000px #111827 inset !important;
  -webkit-text-fill-color: #e5e7eb !important;
  transition: background-color 9999s ease-in-out 0s;
}

/* ============================= */
/* BOTÃO PRINCIPAL */
/* ============================= */

.login-btn {
  background-color: #11b981 !important;
  color: #ffffff !important;
  border-radius: 14px;
  font-weight: 600;
  text-transform: none;
  transition: all 0.2s ease;
}

.login-btn:hover {
  background-color: #0ea371 !important;
  transform: translateY(-1px);
}

/* ============================= */
/* GOOGLE */
/* ============================= */

/* ============================= */
/* LINK */
/* ============================= */

.auth-link {
  color: #11b981;
  font-weight: 500;
  text-decoration: none;
}

.auth-link:hover {
  color: #0ea371;
}

</style>